<template>
  <div>
    <!-- In Impostazioni compare solo il riquadro di lancio: l'editor vero vive in un dialog a tutto schermo -->
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-table-cog</v-icon>
        {{ t('admConfig.columnsEditor.title') }}
        <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
          {{ t('admConfig.columnsEditor.unsaved') }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ t('admConfig.columnsEditor.description') }}
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn color="primary" prepend-icon="mdi-view-column" @click="openEditor">
            {{ t('admConfig.columnsEditor.openEditor') }}
          </v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.columnsEditor.columnCount', { visible: visibleCount, total: totalCount }) }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor a tutto schermo -->
    <v-dialog
      v-model="editorDialog"
      fullscreen
      :scrim="false"
      transition="dialog-bottom-transition"
    >
      <v-card class="d-flex flex-column">
        <v-toolbar density="compact" color="primary" dark class="flex-shrink-0">
          <v-btn icon @click="requestClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ t('admConfig.columnsEditor.title') }}</v-toolbar-title>
          <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
            {{ t('admConfig.columnsEditor.unsaved') }}
          </v-chip>
          <v-spacer />
          <v-btn
            variant="text"
            prepend-icon="mdi-undo"
            :disabled="!hasChanges || saving"
            @click="resetChanges"
          >
            {{ t('admConfig.columnsEditor.discard') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="white"
            class="text-primary ml-2"
            prepend-icon="mdi-content-save"
            :loading="saving"
            :disabled="!hasChanges"
            @click="save"
          >
            {{ t('admConfig.columnsEditor.save') }}
          </v-btn>
        </v-toolbar>

        <v-card-text class="overflow-y-auto flex-grow-1">
          <v-alert v-if="saveError" type="error" variant="tonal" density="comfortable" class="mb-3">
            <div>{{ saveError }}</div>
            <ul v-if="saveErrorDetails.length" class="mt-2 ml-4">
              <li v-for="(e, i) in saveErrorDetails" :key="i" class="text-body-2">{{ e }}</li>
            </ul>
          </v-alert>

          <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-3">
            {{ saveSuccess }}
          </v-alert>

          <!-- Un tab per tipo di processo, più il set predefinito -->
          <v-tabs v-model="activeSet" density="compact" color="primary" class="mb-3">
            <v-tab v-for="pt in projectTypes" :key="pt.id" :value="String(pt.id)">
              {{ pt.descr }}
              <v-chip size="x-small" label class="ml-2">{{ countForSet(String(pt.id)) }}</v-chip>
            </v-tab>
            <v-tab value="default">
              {{ t('admConfig.columnsEditor.tabDefault') }}
              <v-chip size="x-small" label class="ml-2">{{ countForSet('default') }}</v-chip>
            </v-tab>
          </v-tabs>

          <v-alert
            v-if="activeSet === 'default'"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ t('admConfig.columnsEditor.tabDefaultHint') }}
          </v-alert>

          <!-- Ordinamento iniziale del set attivo -->
          <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
            <v-select
              v-model="sortKey"
              :items="sortableItems"
              item-title="title"
              item-value="value"
              :label="t('admConfig.columnsEditor.defaultSort')"
              density="compact"
              hide-details
              clearable
              style="max-width: 280px;"
            />
            <v-btn-toggle
              v-model="sortOrder"
              density="compact"
              variant="outlined"
              divided
              mandatory
              :disabled="!sortKey"
            >
              <v-btn value="asc" size="small">
                <v-icon size="small" class="mr-1">mdi-sort-ascending</v-icon>
                {{ t('admConfig.columnsEditor.sortAsc') }}
              </v-btn>
              <v-btn value="desc" size="small">
                <v-icon size="small" class="mr-1">mdi-sort-descending</v-icon>
                {{ t('admConfig.columnsEditor.sortDesc') }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Azioni sul set attivo -->
          <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
            <v-menu :close-on-content-click="false" v-model="addMenu">
              <template v-slot:activator="{ props }">
                <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-plus" v-bind="props">
                  {{ t('admConfig.columnsEditor.addColumn') }}
                </v-btn>
              </template>
              <v-card min-width="380" max-width="460">
                <v-list density="compact" max-height="420" class="overflow-y-auto">
                  <template v-for="group in candidateGroups" :key="group.title">
                    <v-list-subheader v-if="group.items.length">{{ group.title }}</v-list-subheader>
                    <v-list-item
                      v-for="cand in group.items"
                      :key="cand.key"
                      @click="addColumn(cand)"
                    >
                      <v-list-item-title>{{ cand.title }}</v-list-item-title>
                      <v-list-item-subtitle><code>{{ cand.key }}</code></v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-list>
                <v-divider />
                <div class="pa-3">
                  <div class="text-caption text-medium-emphasis mb-1">
                    {{ t('admConfig.columnsEditor.groupCustom') }}
                  </div>
                  <div class="d-flex align-center" style="gap: 8px;">
                    <v-text-field
                      v-model="customKey"
                      :label="t('admConfig.columnsEditor.customKeyLabel')"
                      :hint="t('admConfig.columnsEditor.customKeyHint')"
                      :error-messages="customKeyError"
                      density="compact"
                      persistent-hint
                      @keyup.enter="addCustomColumn"
                    />
                    <v-btn size="small" color="primary" variant="tonal" @click="addCustomColumn">
                      {{ t('admConfig.columnsEditor.customKeyAdd') }}
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-menu>

            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  size="small"
                  variant="text"
                  prepend-icon="mdi-content-duplicate"
                  v-bind="props"
                  :disabled="otherSets.length === 0"
                >
                  {{ t('admConfig.columnsEditor.copyFrom') }}
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item v-for="s in otherSets" :key="s.value" @click="copyFrom(s.value)">
                  <v-list-item-title>{{ s.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn size="small" variant="text" prepend-icon="mdi-restore" @click="restoreDefaults">
              {{ t('admConfig.columnsEditor.restoreDefaults') }}
            </v-btn>

            <v-spacer />
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.columnsEditor.totalWidth', { width: totalWidth }) }}
            </span>
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.columnsEditor.dragHint') }}
            </span>
          </div>

          <v-alert
            v-if="!activeColumns.length"
            type="info"
            variant="tonal"
            density="comfortable"
          >
            {{ t('admConfig.columnsEditor.emptySet') }}
          </v-alert>

          <!-- Colonne del set attivo, nell'ordine in cui appariranno in tabella -->
          <div
            v-for="(col, index) in activeColumns"
            :key="`${col.key}-${index}`"
            class="column-row d-flex align-center pa-2 rounded"
            :class="{ 'column-row--dragging': dragIndex === index, 'column-row--hidden': col.visible === false }"
            draggable="true"
            @dragstart="onDragStart(index)"
            @dragend="dragIndex = null"
            @dragover.prevent
            @drop.prevent="onDrop(index)"
          >
            <v-icon size="small" color="grey" class="mr-2 drag-handle">mdi-drag</v-icon>

            <v-checkbox-btn
              :model-value="col.visible !== false"
              density="compact"
              class="flex-grow-0 mr-2"
              :title="t('admConfig.columnsEditor.visible')"
              @update:model-value="v => setVisible(col, v)"
            />

            <div class="flex-grow-1" style="min-width: 0;">
              <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                <span class="text-body-2 font-weight-medium">{{ resolveTitle(col) }}</span>
                <code class="text-caption text-medium-emphasis">{{ col.key }}</code>
              </div>
              <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                <v-chip v-if="isPseudo(col.key)" size="x-small" label variant="tonal" color="blue-grey">
                  {{ t('admConfig.columnsEditor.systemColumn') }}
                </v-chip>
                <v-chip v-else-if="isDerived(col.key)" size="x-small" label variant="tonal" color="deep-purple">
                  {{ t('admConfig.columnsEditor.derivedColumn') }}
                </v-chip>
                <v-chip v-if="col.label" size="x-small" label variant="tonal" color="teal">
                  {{ col.label }}
                </v-chip>
                <v-chip v-if="col.cellHighlight" size="x-small" label variant="tonal" color="orange">
                  {{ t('admConfig.columnsEditor.cellHighlightReminder') }}
                </v-chip>
                <v-chip v-if="isUnknownKey(col.key)" size="x-small" label variant="tonal" color="warning">
                  {{ t('admConfig.columnsEditor.unknownKeyWarning') }}
                </v-chip>
              </div>
            </div>

            <v-select
              :model-value="col.formatter || 'raw'"
              :items="formatterItems"
              item-title="title"
              item-value="value"
              :label="t('admConfig.columnsEditor.formatter')"
              density="compact"
              hide-details
              class="mr-2"
              style="max-width: 190px;"
              :disabled="isPseudo(col.key)"
              @update:model-value="v => col.formatter = v"
            />

            <v-text-field
              :model-value="col.width ?? null"
              type="number"
              :label="t('admConfig.columnsEditor.widthPx')"
              density="compact"
              hide-details
              class="mr-2"
              style="max-width: 110px;"
              @update:model-value="v => setWidth(col, v)"
            />

            <v-checkbox-btn
              :model-value="col.sortable !== false && !isPseudo(col.key)"
              density="compact"
              class="flex-grow-0 mr-1"
              :disabled="isPseudo(col.key)"
              :title="t('admConfig.columnsEditor.sortable')"
              @update:model-value="v => col.sortable = v"
            />

            <v-menu :close-on-content-click="false">
              <template v-slot:activator="{ props }">
                <v-btn icon variant="text" size="small" v-bind="props">
                  <v-icon size="small">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-card min-width="340">
                <v-card-text>
                  <v-text-field
                    :model-value="col.label || ''"
                    :label="t('admConfig.columnsEditor.labelOverride')"
                    :hint="t('admConfig.columnsEditor.labelOverrideHint')"
                    density="compact"
                    persistent-hint
                    clearable
                    counter="60"
                    class="mb-3"
                    @update:model-value="v => setLabel(col, v)"
                  />
                  <v-select
                    :model-value="col.align || null"
                    :items="alignItems"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.columnsEditor.align')"
                    density="compact"
                    class="mb-3"
                    @update:model-value="v => setAlign(col, v)"
                  />
                  <v-select
                    :model-value="col.cellHighlight || null"
                    :items="highlightItems"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.columnsEditor.cellHighlight')"
                    density="compact"
                    :disabled="isPseudo(col.key)"
                    @update:model-value="v => setHighlight(col, v)"
                  />
                </v-card-text>
                <v-divider />
                <v-card-actions>
                  <v-btn color="error" variant="text" size="small" prepend-icon="mdi-delete" @click="removeColumn(index)">
                    {{ t('admConfig.columnsEditor.remove') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>

          <!-- Anteprima delle sole intestazioni: verifica ordine, larghezze ed etichette senza salvare -->
          <v-expansion-panels variant="accordion" class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-body-2">
                {{ t('admConfig.columnsEditor.preview') }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="overflow-x-auto">
                  <table class="preview-table">
                    <thead>
                      <tr>
                        <th
                          v-for="col in previewColumns"
                          :key="col.key"
                          :style="col.width ? `width: ${col.width}px; min-width: ${col.width}px;` : ''"
                        >
                          {{ col.key === 'data-table-expand' ? '' : resolveTitle(col) }}
                        </th>
                      </tr>
                    </thead>
                  </table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-title class="text-body-2">
                {{ t('admConfig.columnsEditor.jsonPreview') }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <pre class="json-preview">{{ jsonPreview }}</pre>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <!-- Conferma chiusura con modifiche pendenti -->
        <v-dialog v-model="confirmCloseDialog" max-width="480" persistent>
          <v-card>
            <v-card-title>{{ t('admConfig.columnsEditor.confirmCloseTitle') }}</v-card-title>
            <v-card-text class="text-body-2">
              {{ t('admConfig.columnsEditor.confirmCloseText') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="confirmCloseDialog = false">
                {{ t('admConfig.columnsEditor.keepEditing') }}
              </v-btn>
              <v-btn variant="text" color="error" @click="discardAndClose">
                {{ t('admConfig.columnsEditor.discardAndClose') }}
              </v-btn>
              <v-btn variant="flat" color="primary" :loading="saving" @click="saveAndClose">
                {{ t('admConfig.columnsEditor.saveAndClose') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'

const { t, te } = useI18n()
const projectStore = useProjectStore()

// Formatter ammessi: stessa lista di CELL_FORMATTERS in ProjectsTable.vue e di
// AllowedColumnFormatters nel Backend
const FORMATTERS = [
  'raw', 'date', 'dateTime', 'services', 'transportType', 'status', 'statusIcon',
  'daysDelayed', 'projectType', 'currency', 'todoList', 'none'
]
const PSEUDO_KEYS = ['data-table-expand', 'actions']
// Colonne calcolate, senza un campo corrispondente nella definizione campi: il chip "derivata" e, soprattutto,
// nessun falso avviso di chiave sconosciuta
const DERIVED_KEYS = ['daysDelayed', 'todo']
const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]*$/

// Preimpostazione del formatter in base al tipo dichiarato per il campo
const FORMATTER_BY_FIELD_TYPE = {
  date: 'date',
  datetime: 'dateTime',
  currency: 'currency',
  multiselect: 'services',
  servicequantity: 'services'
}

// Etichette già tradotte sotto projects.table.* ma senza colonna che le usi
const UNUSED_LABEL_KEYS = [
  { key: 'projectType', i18nKey: 'projects.table.type', formatter: 'projectType' },
  { key: 'client', i18nKey: 'projects.table.client', formatter: 'raw' },
  { key: 'createdAt', i18nKey: 'projects.table.createdAt', formatter: 'date' }
]

// Set di colonne predefinito: identico a DEFAULT_COLUMN_SET in ProjectsTable.vue
const defaultColumnSet = () => ({
  defaultSort: { key: 'reminderDate', order: 'asc' },
  columns: [
    { key: 'data-table-expand', visible: true, width: 48, sortable: false, formatter: 'none' },
    { key: 'idProject', i18nKey: 'projects.table.id', visible: true, width: 150, sortable: true, formatter: 'raw' },
    { key: 'reference', i18nKey: 'projects.table.reference', visible: true, width: 120, sortable: true, formatter: 'raw' },
    { key: 'transportType', i18nKey: 'projects.table.transportType', visible: true, width: 120, sortable: true, formatter: 'transportType' },
    { key: 'servicesProvided', i18nKey: 'projects.table.services', visible: true, width: 120, sortable: true, formatter: 'services' },
    { key: 'activeStatus', i18nKey: 'projects.table.status', visible: true, width: 140, sortable: true, formatter: 'status' },
    { key: 'daysDelayed', i18nKey: 'projects.table.daysDelayed', visible: true, width: 110, sortable: true, formatter: 'daysDelayed' },
    { key: 'reminderDate', i18nKey: 'projects.table.dueDate', visible: true, width: 110, sortable: true, formatter: 'date', cellHighlight: 'reminderAlert' },
    { key: 'estimatedDepartureDate', i18nKey: 'projects.table.estimatedDeparture', visible: true, width: 110, sortable: true, formatter: 'date' },
    { key: 'estimatedArriveDate', i18nKey: 'projects.table.estimatedArrival', visible: true, width: 110, sortable: true, formatter: 'date' },
    { key: 'numeraryValueDate', i18nKey: 'projects.table.numeraryDate', visible: true, width: 110, sortable: true, formatter: 'date' },
    { key: 'actions', i18nKey: 'projects.table.actions', visible: true, width: 80, sortable: false, formatter: 'none' }
  ]
})

// Copia di lavoro: nulla tocca il file finché non si preme Salva
const workingConfig = ref({ version: 1, byProjectType: {}, default: defaultColumnSet() })
const originalJson = ref('')
const activeSet = ref('0')
const saving = ref(false)
const saveError = ref('')
const saveErrorDetails = ref([])
const saveSuccess = ref('')
const dragIndex = ref(null)
const addMenu = ref(false)
const customKey = ref('')
const customKeyError = ref('')

const projectTypes = computed(() => projectStore.projectTypes || [])

const hasChanges = computed(() => JSON.stringify(workingConfig.value) !== originalJson.value)
const jsonPreview = computed(() => JSON.stringify(workingConfig.value, null, 2))

const isPseudo = (key) => PSEUDO_KEYS.includes(key)
const isDerived = (key) => DERIVED_KEYS.includes(key)

const loadFromStore = () => {
  const raw = projectStore.tableColumns
  const cloned = raw ? JSON.parse(JSON.stringify(raw)) : null

  const config = {
    version: 1,
    byProjectType: cloned?.byProjectType && typeof cloned.byProjectType === 'object' ? cloned.byProjectType : {},
    default: cloned?.default || defaultColumnSet()
  }

  // Un tipo di processo senza set proprio parte dalle colonne predefinite, così l'editor
  // non mostra mai un gruppo vuoto solo perché il file non lo elencava
  projectTypes.value.forEach(pt => {
    const key = String(pt.id)
    if (!Array.isArray(config.byProjectType[key]?.columns)) {
      config.byProjectType[key] = defaultColumnSet()
    }
  })

  workingConfig.value = config
  originalJson.value = JSON.stringify(config)
}

const resetChanges = () => {
  loadFromStore()
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
}

// ---------- Set attivo ----------

const setByName = (name) =>
  name === 'default' ? workingConfig.value.default : workingConfig.value.byProjectType[name]

const activeColumns = computed(() => setByName(activeSet.value)?.columns || [])

const countForSet = (name) => (setByName(name)?.columns || []).filter(c => c.visible !== false).length

const totalCount = computed(() => activeColumns.value.length)
const visibleCount = computed(() => activeColumns.value.filter(c => c.visible !== false).length)

const totalWidth = computed(() =>
  activeColumns.value
    .filter(c => c.visible !== false)
    .reduce((sum, c) => sum + (Number(c.width) || 0), 0)
)

const previewColumns = computed(() => activeColumns.value.filter(c => c.visible !== false))

const otherSets = computed(() => {
  const list = projectTypes.value
    .map(pt => ({ value: String(pt.id), title: pt.descr }))
    .concat([{ value: 'default', title: t('admConfig.columnsEditor.tabDefault') }])
  return list.filter(s => s.value !== activeSet.value)
})

// ---------- Etichette ----------

// Stessa precedenza di columnTitle in ProjectsTable.vue, così l'editor mostra il titolo reale
const resolveTitle = (col) => {
  if (col.label) return col.label
  if (col.i18nKey && te(col.i18nKey)) return t(col.i18nKey)
  const conventional = `projects.table.${col.key}`
  if (te(conventional)) return t(conventional)
  return fieldLabelFor(col.key) || col.key
}

const fieldsForActiveSet = computed(() => {
  // Set 'default': l'unione di tutti i tipi, deduplicata per nome. Da quando i campi vivono in
  // un file per tipo, lo stesso nome compare in più tipi e il selettore lo mostrerebbe doppio.
  if (activeSet.value === 'default') {
    const fields = projectStore.fields
    if (!Array.isArray(fields)) return []
    return fields.filter((f, i, arr) => arr.findIndex(o => o.name === f.name) === i)
  }
  return projectStore.fieldsByProjectType?.[activeSet.value] || []
})

const fieldLabelFor = (key) => {
  const match = fieldsForActiveSet.value.find(f => f.name === key)
  return match?.label || null
}

// Chiave non riconducibile a un campo del tipo attivo né a una colonna di sistema: la cella
// resterà vuota. Segnalata ma non bloccata: alcune chiavi valide non corrispondono a nessun campo dichiarato.
const isUnknownKey = (key) => {
  if (isPseudo(key) || isDerived(key)) return false
  if (!Array.isArray(projectStore.fields)) return false
  return !fieldsForActiveSet.value.some(f => f.name === key)
}

// ---------- Voci dei select ----------

const formatterItems = computed(() =>
  FORMATTERS.map(f => ({ value: f, title: t(`admConfig.columnsEditor.formatters.${f}`) }))
)

const alignItems = computed(() => [
  { value: null, title: t('admConfig.columnsEditor.alignDefault') },
  { value: 'start', title: t('admConfig.columnsEditor.alignStart') },
  { value: 'center', title: t('admConfig.columnsEditor.alignCenter') },
  { value: 'end', title: t('admConfig.columnsEditor.alignEnd') }
])

const highlightItems = computed(() => [
  { value: null, title: t('admConfig.columnsEditor.cellHighlightNone') },
  { value: 'reminderAlert', title: t('admConfig.columnsEditor.cellHighlightReminder') }
])

// Solo colonne visibili, ordinabili e non pseudo: la stessa regola che il Backend valida,
// così l'ordinamento iniziale non può essere impostato su una colonna non valida
const sortableItems = computed(() =>
  activeColumns.value
    .filter(c => c.visible !== false && c.sortable !== false && !isPseudo(c.key))
    .map(c => ({ value: c.key, title: resolveTitle(c) }))
)

const sortKey = computed({
  get: () => setByName(activeSet.value)?.defaultSort?.key || null,
  set: (value) => {
    const set = setByName(activeSet.value)
    if (!set) return
    if (!value) {
      delete set.defaultSort
      return
    }
    set.defaultSort = { key: value, order: set.defaultSort?.order === 'desc' ? 'desc' : 'asc' }
  }
})

const sortOrder = computed({
  get: () => setByName(activeSet.value)?.defaultSort?.order || 'asc',
  set: (value) => {
    const set = setByName(activeSet.value)
    if (set?.defaultSort) set.defaultSort.order = value === 'desc' ? 'desc' : 'asc'
  }
})

// Se la colonna dell'ordinamento iniziale viene nascosta, resa non ordinabile o rimossa,
// l'impostazione va tolta: altrimenti il salvataggio verrebbe rifiutato dal Backend
watch(sortableItems, (items) => {
  const set = setByName(activeSet.value)
  const current = set?.defaultSort?.key
  if (current && !items.some(i => i.value === current)) {
    delete set.defaultSort
  }
})

// ---------- Modifica delle proprietà ----------

const setVisible = (col, value) => { col.visible = !!value }

const setWidth = (col, value) => {
  const n = parseInt(value)
  if (Number.isFinite(n) && n > 0) col.width = Math.min(n, 2000)
  else delete col.width
}

const setLabel = (col, value) => {
  const trimmed = (value || '').trim()
  if (trimmed) col.label = trimmed.slice(0, 60)
  else delete col.label
}

const setAlign = (col, value) => {
  if (value) col.align = value
  else delete col.align
}

const setHighlight = (col, value) => {
  if (value) col.cellHighlight = value
  else delete col.cellHighlight
}

// ---------- Aggiunta e rimozione ----------

const candidateGroups = computed(() => {
  const present = new Set(activeColumns.value.map(c => c.key))

  const system = [
    { key: 'data-table-expand', title: t('admConfig.columnsEditor.systemColumn'), width: 48, sortable: false, formatter: 'none' },
    { key: 'actions', i18nKey: 'projects.table.actions', title: t('projects.table.actions'), width: 80, sortable: false, formatter: 'none' },
    { key: 'daysDelayed', i18nKey: 'projects.table.daysDelayed', title: t('projects.table.daysDelayed'), width: 110, formatter: 'daysDelayed' },
    { key: 'todo', i18nKey: 'projects.table.todo', title: t('projects.table.todo'), width: 220, formatter: 'todoList' }
  ].filter(c => !present.has(c.key))

  const fields = fieldsForActiveSet.value
    .filter(f => !present.has(f.name))
    // Lo stesso name può esistere in più tipi di processo: nel menu basta una voce
    .filter((f, i, arr) => arr.findIndex(o => o.name === f.name) === i)
    .map(f => ({
      key: f.name,
      title: f.label || f.name,
      width: 120,
      formatter: FORMATTER_BY_FIELD_TYPE[f.type] || 'raw'
    }))

  const labels = UNUSED_LABEL_KEYS
    .filter(c => !present.has(c.key))
    .map(c => ({ ...c, title: te(c.i18nKey) ? t(c.i18nKey) : c.key, width: 120 }))

  return [
    { title: t('admConfig.columnsEditor.groupSystem'), items: system },
    { title: t('admConfig.columnsEditor.groupUnusedLabels'), items: labels },
    { title: t('admConfig.columnsEditor.groupFields'), items: fields }
  ]
})

const addColumn = (candidate) => {
  const set = setByName(activeSet.value)
  if (!set) return
  const column = {
    key: candidate.key,
    visible: true,
    width: candidate.width ?? 120,
    sortable: candidate.sortable !== false && !isPseudo(candidate.key),
    formatter: candidate.formatter || 'raw'
  }
  if (candidate.i18nKey) column.i18nKey = candidate.i18nKey
  // Le azioni restano in fondo: è il posto in cui l'utente le cerca
  const actionsIndex = set.columns.findIndex(c => c.key === 'actions')
  if (actionsIndex !== -1 && candidate.key !== 'actions') set.columns.splice(actionsIndex, 0, column)
  else set.columns.push(column)
  addMenu.value = false
}

const addCustomColumn = () => {
  const key = (customKey.value || '').trim()
  customKeyError.value = ''
  if (!KEY_PATTERN.test(key)) {
    customKeyError.value = t('admConfig.columnsEditor.customKeyInvalid')
    return
  }
  if (activeColumns.value.some(c => c.key === key)) {
    customKeyError.value = t('admConfig.columnsEditor.customKeyExists')
    return
  }
  addColumn({ key, width: 120, formatter: 'raw' })
  customKey.value = ''
}

const removeColumn = (index) => {
  const set = setByName(activeSet.value)
  if (set) set.columns.splice(index, 1)
}

const copyFrom = (name) => {
  const source = setByName(name)
  const target = setByName(activeSet.value)
  if (!source || !target) return
  target.columns = JSON.parse(JSON.stringify(source.columns))
  if (source.defaultSort) target.defaultSort = { ...source.defaultSort }
  else delete target.defaultSort
}

const restoreDefaults = () => {
  const target = setByName(activeSet.value)
  if (!target) return
  const fresh = defaultColumnSet()
  target.columns = fresh.columns
  target.defaultSort = fresh.defaultSort
}

// ---------- Riordino drag & drop ----------

const onDragStart = (index) => { dragIndex.value = index }

const onDrop = (targetIndex) => {
  const from = dragIndex.value
  dragIndex.value = null
  if (from === null || from === targetIndex) return

  const set = setByName(activeSet.value)
  if (!set) return

  const arr = set.columns.slice()
  const target = arr[targetIndex]
  const [moved] = arr.splice(from, 1)
  const newTargetIndex = arr.indexOf(target)
  // Trascinando verso il basso la colonna si posiziona dopo il target, verso l'alto prima
  arr.splice(from < targetIndex ? newTargetIndex + 1 : newTargetIndex, 0, moved)
  set.columns = arr
}

// ---------- Apertura / chiusura ----------

const editorDialog = ref(false)
const confirmCloseDialog = ref(false)

const openEditor = async () => {
  // Riallinea alla configurazione sul server, ma senza buttare via un lavoro in corso
  if (!hasChanges.value) {
    await projectStore.loadConfigurations()
    resetChanges()
  }
  const valid = projectTypes.value.map(pt => String(pt.id)).concat('default')
  if (!valid.includes(activeSet.value)) {
    activeSet.value = valid[0]
  }
  editorDialog.value = true
}

const requestClose = () => {
  if (hasChanges.value) {
    confirmCloseDialog.value = true
    return
  }
  editorDialog.value = false
}

const discardAndClose = () => {
  resetChanges()
  confirmCloseDialog.value = false
  editorDialog.value = false
}

const saveAndClose = async () => {
  await save()
  // Con errore di salvataggio si resta nell'editor: chiudere perderebbe le modifiche
  if (!saveError.value) {
    confirmCloseDialog.value = false
    editorDialog.value = false
  } else {
    confirmCloseDialog.value = false
  }
}

onMounted(async () => {
  if (!projectStore.configurations?.config) {
    await projectStore.loadConfigurations()
  }
  loadFromStore()
  if (projectTypes.value.length) activeSet.value = String(projectTypes.value[0].id)
})

// ---------- Salvataggio ----------

const save = async () => {
  saving.value = true
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  try {
    const result = await projectStore.saveColumns(workingConfig.value)
    loadFromStore()
    saveSuccess.value = t('admConfig.columnsEditor.saved', {
      count: result?.count ?? 0,
      backup: result?.backup || '—'
    })
  } catch (e) {
    saveError.value = e?.message || t('admConfig.columnsEditor.saveError')
    const details = e?.response?.data?.errors
    if (Array.isArray(details)) saveErrorDetails.value = details
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.column-row {
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;
  cursor: grab;
  background: rgba(255, 255, 255, 0.6);
}
.column-row:hover {
  background: rgba(0, 0, 0, 0.03);
}
.column-row--dragging {
  opacity: 0.4;
}
.column-row--hidden {
  opacity: 0.55;
}
.drag-handle {
  cursor: grab;
}
.json-preview {
  max-height: 360px;
  overflow: auto;
  font-size: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  padding: 8px;
  border-radius: 4px;
}
.preview-table {
  border-collapse: collapse;
  white-space: nowrap;
}
.preview-table th {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 6px 10px;
  font-size: 0.8rem;
  text-align: left;
  background: rgba(0, 0, 0, 0.03);
}
</style>
